import type {
  ContributorDto,
  ContributorRole,
  FlowContributorRole,
  SessionContributorRole,
  EventContributorRole,
  RoleDto,
} from '@/types/dtos/contributors/response/contributor.dto';
import { EVENT_CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  collectRemoteSpeakerAggregateColorForContext,
  worstContributorStatusColor,
} from '@/utils/remote-speaker-status.utils';

/** Mode document id + master origin id — roles may reference either. */
export function getSessionRoleLookupIds(session: Pick<SessionDto, 'id' | 'origin'>): string[] {
  const ids = new Set<string>();
  if (session.id) ids.add(session.id);
  if (session.origin) ids.add(getObjectId(session.origin));
  return Array.from(ids);
}

function contributorSessionRoleMatchesLookup(role: ContributorDto['roles'][number], lookupIds: Set<string>): boolean {
  if (role.entityType !== 'Session') return false;
  if (typeof role.entity === 'string') {
    return lookupIds.has(role.entity);
  }
  const entityId = role.entity?.id;
  const entityOrigin = role.entity?.origin != null ? getObjectId(role.entity.origin) : undefined;
  return (entityId != null && lookupIds.has(entityId)) || (entityOrigin != null && lookupIds.has(entityOrigin));
}

export const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

/**
 * Extracts contributors for a specific flow from the roles-based system
 * @param flow The flow to get contributors for
 * @param allContributors All contributors from the event store
 */
export function getFlowContributors(
  flow: FlowDto,
  allContributors: ContributorDto[] = [],
): ContributorDto<FlowContributorRole>[] {
  // Always use the global contributors store for consistent entity population
  // Don't fall back to flow.contributors as they may not have proper entity population
  const flowIds = new Set<string>([normalizeId(flow.id), normalizeId(flow.origin)].filter(Boolean) as string[]);
  return allContributors.filter((contributor) =>
    contributor.roles.some(
      (role) =>
        role.entityType === 'Flow' &&
        (typeof role.entity === 'string'
          ? flowIds.has(role.entity)
          : flowIds.has(role.entity?.id || '') ||
            flowIds.has(typeof role.entity?.origin === 'string' ? role.entity.origin : role.entity?.origin?.id || '')),
    ),
  ) as ContributorDto<FlowContributorRole>[];
}

/**
 * Extracts contributors for a specific session from the roles-based system
 * @param session The session to get contributors for
 * @param allContributors All contributors from the event store
 */
export function getSessionContributors(
  session: SessionDto,
  allContributors: ContributorDto[] = [],
): ContributorDto<SessionContributorRole>[] {
  // Always use the global contributors store for consistent entity population
  // Don't fall back to session.contributors as they may not have proper entity population
  const lookupIds = new Set(getSessionRoleLookupIds(session));
  return allContributors.filter((contributor) =>
    contributor.roles.some((role) => contributorSessionRoleMatchesLookup(role, lookupIds)),
  ) as ContributorDto<SessionContributorRole>[];
}

/**
 * Gets contributors with a specific role for a flow
 */
export function getFlowContributorsByRole(
  flow: FlowDto,
  role: FlowContributorRole,
  allContributors: ContributorDto[] = [],
): ContributorDto<FlowContributorRole>[] {
  const contributors = getFlowContributors(flow, allContributors);
  const flowId = flow.origin || flow.id;
  return contributors.filter((contributor) =>
    contributor.roles.some(
      (r) =>
        r.role === role &&
        r.entityType === 'Flow' &&
        (typeof r.entity === 'string' ? r.entity === flowId : r.entity?.id === flowId || r.entity?.origin === flowId),
    ),
  );
}

/**
 * Gets contributors with a specific role for a session
 */
export function getSessionContributorsByRole(
  session: SessionDto,
  role: SessionContributorRole,
  allContributors: ContributorDto[] = [],
): ContributorDto<SessionContributorRole>[] {
  const contributors = getSessionContributors(session, allContributors);
  const sessionId = session.origin || session.id;
  return contributors.filter((contributor) =>
    contributor.roles.some(
      (r) =>
        r.role === role &&
        r.entityType === 'Session' &&
        (typeof r.entity === 'string'
          ? r.entity === sessionId
          : r.entity?.id === sessionId || r.entity?.origin === sessionId),
    ),
  );
}

/**
 * Gets moderators for a flow
 * For single-flow events (multiFlow=false), also includes event-level moderators
 */
export function getFlowModerators(
  flow: FlowDto,
  allContributors: ContributorDto[] = [],
  eventMultiFlow?: boolean,
): ContributorDto<FlowContributorRole>[] {
  const flowModerators = getFlowContributorsByRole(flow, 'moderator', allContributors);

  // If multiFlow=false, also include event-level moderators
  if (eventMultiFlow === false) {
    const eventModerators = allContributors.filter((contributor) =>
      contributor.roles.some((r) => r.role === 'moderator' && r.entityType === 'Event'),
    );

    // Combine and deduplicate
    const allModerators = [...flowModerators];
    eventModerators.forEach((eventModerator) => {
      if (!allModerators.some((fm) => fm.id === eventModerator.id)) {
        allModerators.push(eventModerator as ContributorDto<FlowContributorRole>);
      }
    });

    return allModerators;
  }

  return flowModerators;
}

/**
 * Per role row: invite vs strict completion (role + context slot both accepted when a context slot applies).
 * `fullyInvited` means every applicable level has an invite sent (role, and context slot when a slot applies).
 * `rolePending` / `slotPending` mean an invitation was sent at that level but not yet accepted.
 */
export type RoleContextInviteUnit = {
  /** Role and/or context slot has been invited (OR). Used for invite counts. */
  invited: boolean;
  /** Every applicable level has been invited (role, and context slot when a slot applies). */
  fullyInvited: boolean;
  fullyAccepted: boolean;
  rolePending: boolean;
  slotPending: boolean;
};

/** PLAN list row slot id → ORGANIZE flow slot id on contributor roleSlots (`slot.origin` link). */
export function resolveOrganizeFlowSlotIdForInvite(
  slot: { id?: string } | undefined,
  flowOrganize?: FlowDto,
): string | undefined {
  if (!slot?.id) return undefined;
  if (!flowOrganize?.slots?.length) return slot.id;
  if (flowOrganize.slots.some((organizeSlot) => organizeSlot.id === slot.id)) return slot.id;
  return flowOrganize.slots.find((organizeSlot) => organizeSlot.origin === slot.id)?.id ?? slot.id;
}

export function resolveSessionOriginId(session: SessionDto): string {
  return session.origin ? getObjectId(session.origin) : session.id;
}

/** Session roles for invite indicators — matches stable origin and mode-specific session ids. */
export function getContributorSessionRolesForInvite(
  contributor: ContributorDto,
  session: SessionDto,
): ContributorDto['roles'] {
  const originId = resolveSessionOriginId(session);
  const sessionIds = new Set([originId, session.id]);
  return contributor.roles.filter((role) => {
    if (role.entityType !== 'Session') return false;
    if (typeof role.entity === 'string') return sessionIds.has(role.entity);
    if (!role.entity) return false;
    const entityId = role.entity.id;
    const entityOrigin = role.entity.origin ? getObjectId(role.entity.origin) : undefined;
    return (entityId && sessionIds.has(entityId)) || (entityOrigin && sessionIds.has(entityOrigin));
  });
}

/**
 * Flow-slot context (ORGANIZE list row): success requires `role.acceptedAt` and a matching flow-slot roleSlot
 * with `acceptedAt`. When `flowSlotId` is omitted, only role-level acceptance is required for the slot leg.
 */
export function getRoleInviteUnitForFlowSlotContext(
  role: RoleDto<ContributorRole>,
  flowSlotId: string | undefined,
): RoleContextInviteUnit {
  const roleInvited = Boolean(role.invitedAt);
  const roleAccepted = Boolean(role.acceptedAt);
  const rolePending = roleInvited && !roleAccepted;
  if (!flowSlotId) {
    return {
      invited: roleInvited,
      fullyInvited: roleInvited,
      fullyAccepted: roleAccepted,
      rolePending,
      slotPending: false,
    };
  }
  const matches =
    role.slots?.filter((rs) => {
      if (rs.deleted) return false;
      const entityId = typeof rs.entity === 'string' ? rs.entity : rs.entity.id;
      return entityId === flowSlotId;
    }) ?? [];
  const slotInvited = matches.some((rs) => Boolean(rs.invitedAt));
  const slotAccepted = matches.length > 0 && matches.every((rs) => Boolean(rs.acceptedAt));
  const slotPending = slotInvited && !slotAccepted;
  return {
    invited: roleInvited || slotInvited,
    fullyInvited: roleInvited && slotInvited,
    fullyAccepted: roleAccepted && slotAccepted,
    rolePending,
    slotPending,
  };
}

/**
 * Event-slot context (flow URL `eventSlotId`): success requires `role.acceptedAt` and matching event-slot
 * roleSlot(s) all accepted. When `eventSlotId` is omitted, only role-level acceptance is required for the slot leg.
 *
 * If the role has no active (non-deleted) slots, `slotAccepted` is treated as `true` to avoid
 * false-pending states after soft-deletes (e.g. crew roles with no time-slot assignments).
 */
export function getRoleInviteUnitForEventSlotContext(
  role: RoleDto<ContributorRole>,
  eventSlotId: string | undefined,
): RoleContextInviteUnit {
  const roleInvited = Boolean(role.invitedAt);
  const roleAccepted = Boolean(role.acceptedAt);
  const rolePending = roleInvited && !roleAccepted;
  if (!eventSlotId) {
    return {
      invited: roleInvited,
      fullyInvited: roleInvited,
      fullyAccepted: roleAccepted,
      rolePending,
      slotPending: false,
    };
  }
  const hasAnyActiveSlots = role.slots?.some((rs) => !rs.deleted) ?? false;
  const matches =
    role.slots?.filter((rs) => {
      if (rs.deleted) return false;
      return (
        normalizeId(rs.eventSlotId) === eventSlotId ||
        (rs.entityType === 'EventSlot' && normalizeId(rs.entity) === eventSlotId)
      );
    }) ?? [];
  const slotInvited = matches.some((rs) => Boolean(rs.invitedAt));
  const slotAccepted = !hasAnyActiveSlots || (matches.length > 0 && matches.every((rs) => Boolean(rs.acceptedAt)));
  const slotPending = slotInvited && !slotAccepted;
  // No active slots (e.g. crew): only role-level invite is required, matching `slotAccepted` treatment.
  const fullyInvited = !hasAnyActiveSlots ? roleInvited : roleInvited && slotInvited;
  return {
    invited: roleInvited || slotInvited,
    fullyInvited,
    fullyAccepted: roleAccepted && slotAccepted,
    rolePending,
    slotPending,
  };
}

/** One entry per Flow/Event moderator role row (not merged per contributor). */
export function getFlowModeratorRoleInviteUnits(
  flow: FlowDto,
  allContributors: ContributorDto[],
  eventMultiFlow?: boolean,
  eventSlotId?: string,
): RoleContextInviteUnit[] {
  const moderators = getFlowModerators(flow, allContributors, eventMultiFlow);
  if (moderators.length === 0) return [];

  const flowIds = new Set([normalizeId(flow.id), normalizeId(flow.origin)].filter(Boolean) as string[]);

  const entityMatchesFlow = (entity: unknown): boolean => {
    if (typeof entity === 'string') return flowIds.has(entity);
    if (entity && typeof entity === 'object') {
      const id = normalizeId(entity);
      const origin = normalizeId((entity as { origin?: unknown }).origin);
      return (id !== undefined && flowIds.has(id)) || (origin !== undefined && flowIds.has(origin));
    }
    return false;
  };

  const units: RoleContextInviteUnit[] = [];

  for (const contributor of moderators) {
    for (const role of contributor.roles) {
      const isFlowModerator = role.role === 'moderator' && role.entityType === 'Flow' && entityMatchesFlow(role.entity);
      const isEventModerator = eventMultiFlow === false && role.role === 'moderator' && role.entityType === 'Event';

      if (!isFlowModerator && !isEventModerator) continue;

      units.push(getRoleInviteUnitForEventSlotContext(role, eventSlotId));
    }
  }

  return units;
}

/**
 * Invite/accept aggregates for flow moderators — one unit per moderator role row.
 * `confirmed` counts units that are strictly fully accepted (role + event-slot context when `eventSlotId` is set).
 */
export function getFlowModeratorInviteAggregates(
  flow: FlowDto,
  allContributors: ContributorDto[],
  eventMultiFlow?: boolean,
  eventSlotId?: string,
): { total: number; invited: number; confirmed: number } {
  const units = getFlowModeratorRoleInviteUnits(flow, allContributors, eventMultiFlow, eventSlotId);
  return {
    total: units.length,
    invited: units.filter((u) => u.invited).length,
    confirmed: units.filter((u) => u.fullyAccepted).length,
  };
}

/** MUI `SvgIcon` `color` prop values used by slot list indicators. */
export type FlowContributorStatusIconColor = 'disabled' | 'action' | 'primary' | 'info' | 'success' | 'warning';

/**
 * Maps per-role invite units to a list icon color (moderator start row + slot row people icon).
 * Event-manager perspective: incomplete invite work is a primary task; after every level is invited,
 * waiting on acceptances is info; all accepted is success.
 * - primary: any role or applicable context slot has not been invited yet
 * - info: every applicable level invited, but not all fully accepted yet
 * - success: every unit fully accepted (role + context slot when applicable)
 */
export function resolveRoleInviteUnitsIndicatorColor(units: RoleContextInviteUnit[]): FlowContributorStatusIconColor {
  if (units.length === 0) return 'disabled';
  if (!units.every((u) => u.fullyInvited)) return 'primary';
  if (units.every((u) => u.fullyAccepted)) return 'success';
  return 'info';
}

/**
 * Moderator invite/accept status for the flow start row (slot assets) / event-slot context.
 */
export function getFlowModeratorStatusIconColor(
  flow: FlowDto,
  allContributors: ContributorDto[],
  eventMultiFlow?: boolean,
  eventSlotId?: string,
  event?: EventDto | { slots?: EventDto['slots'] },
): FlowContributorStatusIconColor {
  const units = getFlowModeratorRoleInviteUnits(flow, allContributors, eventMultiFlow, eventSlotId);
  const inviteColor = resolveRoleInviteUnitsIndicatorColor(units);
  const flowId = typeof flow.origin === 'string' ? flow.origin : flow.id;
  const remoteColor = collectRemoteSpeakerAggregateColorForContext({
    allContributors,
    event,
    eventSlotId,
    flowId,
  });
  return worstContributorStatusColor(inviteColor, remoteColor);
}

/**
 * Gets speakers for a session
 */
export function getSessionSpeakers(
  session: SessionDto,
  allContributors: ContributorDto[] = [],
): ContributorDto<SessionContributorRole>[] {
  return getSessionContributorsByRole(session, 'speaker', allContributors);
}

/**
 * Gets panelists for a session
 */
export function getSessionPanelists(
  session: SessionDto,
  allContributors: ContributorDto[] = [],
): ContributorDto<SessionContributorRole>[] {
  return getSessionContributorsByRole(session, 'panelist', allContributors);
}

/**
 * Gets panel hosts for a session
 */
export function getSessionPanelHosts(
  session: SessionDto,
  allContributors: ContributorDto[] = [],
): ContributorDto<SessionContributorRole>[] {
  return getSessionContributorsByRole(session, 'panel_host', allContributors);
}

/**
 * Checks if a contributor has a specific role for a flow
 */
export function hasFlowRole(contributor: ContributorDto, flowId: string, role: FlowContributorRole): boolean {
  return contributor.roles.some(
    (r) =>
      r.role === role &&
      r.entityType === 'Flow' &&
      (typeof r.entity === 'string' ? r.entity === flowId : r.entity?.id === flowId),
  );
}

/**
 * Checks if a contributor has a specific role for a session
 */
export function hasSessionRole(contributor: ContributorDto, sessionId: string, role: SessionContributorRole): boolean {
  return contributor.roles.some(
    (r) =>
      r.role === role &&
      r.entityType === 'Session' &&
      (typeof r.entity === 'string' ? r.entity === sessionId : r.entity?.id === sessionId),
  );
}

/**
 * Gets the role of a contributor for a specific flow
 */
export function getContributorFlowRole(contributor: ContributorDto, flowId: string): FlowContributorRole | null {
  const role = contributor.roles.find(
    (r) => r.entityType === 'Flow' && (typeof r.entity === 'string' ? r.entity === flowId : r.entity?.id === flowId),
  );
  return (role?.role as FlowContributorRole) || null;
}

/**
 * Gets the role of a contributor for a specific session
 */
export function getContributorSessionRole(
  contributor: ContributorDto,
  sessionId: string,
): SessionContributorRole | null {
  const role = contributor.roles.find(
    (r) =>
      r.entityType === 'Session' &&
      (typeof r.entity === 'string' ? r.entity === sessionId : r.entity?.id === sessionId),
  );
  return (role?.role as SessionContributorRole) || null;
}

/**
 * Gets a specific role for a contributor in a flow
 */
export function getContributorFlowRoleById(
  contributor: ContributorDto,
  flowId: string,
  role: FlowContributorRole,
): ContributorDto['roles'][0] | undefined {
  return contributor.roles.find(
    (r) =>
      r.role === role &&
      r.entityType === 'Flow' &&
      (typeof r.entity === 'string' ? r.entity === flowId : r.entity?.id === flowId || r.entity?.origin === flowId),
  );
}

/**
 * Gets a specific role for a contributor in a session
 */
export function getContributorSessionRoleById(
  contributor: ContributorDto,
  sessionId: string,
  role: SessionContributorRole,
): ContributorDto['roles'][0] | undefined {
  return contributor.roles.find(
    (r) =>
      r.role === role &&
      r.entityType === 'Session' &&
      (typeof r.entity === 'string'
        ? r.entity === sessionId
        : r.entity?.id === sessionId || r.entity?.origin === sessionId),
  );
}

/**
 * Gets any role for a contributor in a session (generic)
 */
export function getContributorSessionRoleGeneric(
  contributor: ContributorDto,
  sessionKey: string | Pick<SessionDto, 'id' | 'origin'>,
): ContributorDto['roles'][0] | undefined {
  const lookupIds = new Set(typeof sessionKey === 'string' ? [sessionKey] : getSessionRoleLookupIds(sessionKey));
  return contributor.roles.find((role) => contributorSessionRoleMatchesLookup(role, lookupIds));
}

/**
 * Gets all roles for a contributor in a specific flow
 */
export function getContributorFlowRoles(contributor: ContributorDto, flowId: string): ContributorDto['roles'] {
  return contributor.roles.filter(
    (r) => r.entityType === 'Flow' && (typeof r.entity === 'string' ? r.entity === flowId : r.entity?.id === flowId),
  );
}

/**
 * Gets all roles for a contributor in a specific session
 */
export function getContributorSessionRoles(contributor: ContributorDto, sessionId: string): ContributorDto['roles'] {
  return contributor.roles.filter(
    (r) =>
      r.entityType === 'Session' &&
      (typeof r.entity === 'string' ? r.entity === sessionId : r.entity?.id === sessionId),
  );
}

/**
 * Filters contributor roles to show only those relevant to a specific flow
 * For multi-flow events, shows only:
 * 1. Flow roles for the specific flow
 * 2. Session roles for sessions within the flow's slots
 * For single-flow events (multiFlow=false), shows all roles
 * Also validates entity names and handles session origins
 */
export function getFlowRelevantRoles(
  contributor: ContributorDto,
  flow: FlowDto,
  eventMultiFlow?: boolean,
  getEntityName?: (role: RoleDto<EventContributorRole | FlowContributorRole | SessionContributorRole>) => string,
): ContributorDto['roles'] {
  // If explicitly single-flow event, show all roles (with entity name validation if provided)
  if (eventMultiFlow === false) {
    if (getEntityName) {
      return contributor.roles.filter((role) => {
        if (!role.entity) return false;
        const entityName = getEntityName(role);
        return entityName && entityName !== 'undefined' && entityName !== '-';
      });
    }
    return contributor.roles;
  }

  // For multi-flow events (or when eventMultiFlow is undefined/true), filter to only relevant roles
  const flowIds = new Set<string>([normalizeId(flow.id), normalizeId(flow.origin)].filter(Boolean) as string[]);
  const sessionIds = new Set<string>();
  const flowSlotIds = new Set<string>();

  // Collect all session IDs from flow slots (including origins)
  // Also collect all flow slot IDs
  if (flow.slots) {
    flow.slots.forEach((slot) => {
      // Collect flow slot IDs
      const flowSlotId = normalizeId(slot.id);
      if (flowSlotId) flowSlotIds.add(flowSlotId);

      if (slot.contentType === 'Session' && slot.content) {
        if (typeof slot.content === 'string') {
          // Content is a session ID string
          sessionIds.add(slot.content);
        } else if (typeof slot.content === 'object' && 'id' in slot.content) {
          // Content is a populated SessionDto object
          const sessionId = normalizeId(slot.content.id);
          if (sessionId) sessionIds.add(sessionId);
          // Also add origin ID if it exists
          if ('origin' in slot.content) {
            const originId = normalizeId(slot.content.origin);
            if (originId) sessionIds.add(originId);
          }
        }
      }
    });
  }

  return contributor.roles.filter((role) => {
    // Always check if role.entity exists
    if (!role.entity) return false;

    // FIRST: Check flow/session relevance (this is the main filtering logic)
    if (role.entityType === 'Flow') {
      const roleFlowIds = new Set<string>();
      const directId = normalizeId(role.entity);
      if (directId) roleFlowIds.add(directId);
      if (typeof role.entity === 'object' && role.entity) {
        const originId = normalizeId((role.entity as { origin?: unknown }).origin);
        if (originId) roleFlowIds.add(originId);
      }
      const matches = Array.from(roleFlowIds).some((id) => flowIds.has(id));

      // If it matches the flow, then apply entity validation
      if (matches) {
        // For published entities (object type), check if they have a proper title (not just ID)
        // In flow context, only show entities with proper titles (published entities)
        if (typeof role.entity === 'object' && role.entity) {
          const hasTitle = !!(role.entity as FlowDto | EventDto).title;

          // Only allow entities with proper titles (published entities)
          if (!hasTitle) return false;
        }

        // Validate entity name if getEntityName function is provided
        if (getEntityName) {
          const entityName = getEntityName(role);
          if (!entityName || entityName === 'undefined' || entityName === '-') return false;
        }

        return true;
      }
      return false;
    }

    if (role.entityType === 'Session') {
      const roleSessionIds = new Set<string>();
      const directId = normalizeId(role.entity);
      if (directId) roleSessionIds.add(directId);
      if (typeof role.entity === 'object' && role.entity) {
        const originId = normalizeId((role.entity as { origin?: unknown }).origin);
        if (originId) roleSessionIds.add(originId);
      }
      const matches = Array.from(roleSessionIds).some((id) => sessionIds.has(id));

      // If it matches a session in the flow, check if it has role slots for slots in this flow
      if (matches) {
        // In flow context, only show Session roles that have at least one role slot for a slot in this flow
        const hasRoleSlotForFlowSlot = role.slots?.some((slot) => {
          if (slot.entityType !== 'FlowSlot') return false;
          // Handle both string ID and object with id property
          const slotEntityId = normalizeId(slot.entity);
          return slotEntityId && flowSlotIds.has(slotEntityId);
        });

        // If no role slots exist for slots in this flow, filter out this role
        if (!hasRoleSlotForFlowSlot) return false;

        // For published entities (object type), check if they have a proper title (not just ID)
        // In flow context, only show entities with proper titles (published entities)
        if (typeof role.entity === 'object' && role.entity) {
          const hasTitle = !!(role.entity as FlowDto | EventDto).title;

          // Only allow entities with proper titles (published entities)
          if (!hasTitle) return false;
        }

        // Validate entity name if getEntityName function is provided
        if (getEntityName) {
          const entityName = getEntityName(role);
          if (!entityName || entityName === 'undefined' || entityName === '-') return false;
        }

        return true;
      }
      return false;
    }

    // Don't show Event roles in multi-flow context
    return false;
  });
}

/**
 * Filters contributor roles to show only those with valid entity names
 * Used when not in flow context to show all roles with valid entities
 */
export function getValidEntityRoles(contributor: ContributorDto): ContributorDto['roles'] {
  return (
    contributor.roles?.filter((role) => {
      if (!role.entity) return false;

      if (role.entityType === 'Event') {
        return true;
      }

      // Allow roles with slots to show pending-publication messaging when ORGANIZE data is missing.
      if (role.slots && role.slots.length > 0) {
        return true;
      }

      // For published entities (object type), check if they have a proper title (not just ID)
      if (typeof role.entity === 'object' && role.entity) {
        return !!(role.entity as FlowDto | EventDto).title;
      }

      return false;
    }) || []
  );
}

/**
 * 1. Contributors with direct flow roles (moderators, etc.)
 * 2. Contributors with session roles in the flow's slots
 * 3. ALL contributors if the event has multiFlow=false (single flow events)
 * This is useful for showing all contributors in flow details
 */
export function getAllFlowRelatedContributors(
  flow: FlowDto,
  allContributors: ContributorDto[] = [],
  eventMultiFlow?: boolean,
): ContributorDto[] {
  const relatedContributors = new Set<string>();
  const contributors: ContributorDto[] = [];
  const flowIds = new Set<string>([normalizeId(flow.id), normalizeId(flow.origin)].filter(Boolean) as string[]);
  const flowSlotIds = new Set((flow.slots ?? []).map((slot) => normalizeId(slot.id)).filter(Boolean) as string[]);
  const flowEventSlotIds = new Set<string>();

  // If multiFlow=false, show ALL contributors from the event
  if (eventMultiFlow === false) {
    return allContributors;
  }

  const flowSlotId = normalizeId(flow.slot?.id);
  if (flowSlotId) flowEventSlotIds.add(flowSlotId);
  const flowSlotOriginId = normalizeId(flow.slot?.origin ?? undefined);
  if (flowSlotOriginId) flowEventSlotIds.add(flowSlotOriginId);

  const matchesEventSlot = (eventSlotId?: string) => {
    if (flowEventSlotIds.size === 0) return true;
    const normalizedEventSlotId = normalizeId(eventSlotId);
    if (!normalizedEventSlotId) return false;
    return flowEventSlotIds.has(normalizedEventSlotId);
  };

  const matchesFlowRole = (role: ContributorDto['roles'][0]) => {
    if (role.entityType !== 'Flow') return false;
    const roleFlowIds = new Set<string>();
    const directId = normalizeId(role.entity);
    if (directId) roleFlowIds.add(directId);
    if (typeof role.entity === 'object' && role.entity) {
      const originId = normalizeId((role.entity as { origin?: unknown }).origin);
      if (originId) roleFlowIds.add(originId);
    }
    const matchesFlow = Array.from(roleFlowIds).some((id) => flowIds.has(id));
    if (!matchesFlow) return false;
    if (flowEventSlotIds.size === 0) return true;
    return role.slots?.some((slot) => matchesEventSlot(slot.eventSlotId)) ?? false;
  };

  const matchesSessionRole = (role: ContributorDto['roles'][0], sessionIds: Set<string>) => {
    if (role.entityType !== 'Session') return false;
    const roleSessionIds = new Set<string>();
    const directId = normalizeId(role.entity);
    if (directId) roleSessionIds.add(directId);
    if (typeof role.entity === 'object' && role.entity) {
      const originId = normalizeId((role.entity as { origin?: unknown }).origin);
      if (originId) roleSessionIds.add(originId);
    }
    const matchesSession = Array.from(roleSessionIds).some((id) => sessionIds.has(id));
    if (!matchesSession) return false;
    return (
      role.slots?.some((slot) => {
        if (slot.entityType !== 'FlowSlot') return false;
        const slotEntityId = normalizeId(slot.entity);
        if (!slotEntityId || !flowSlotIds.has(slotEntityId)) return false;
        return matchesEventSlot(slot.eventSlotId);
      }) ?? false
    );
  };

  const sessionIds = new Set<string>();
  flow.slots?.forEach((slot) => {
    if (slot.contentType !== 'Session' || !slot.content) return;
    if (typeof slot.content === 'string') {
      sessionIds.add(slot.content);
      return;
    }
    if (slot.content.id) sessionIds.add(slot.content.id);
    if (typeof slot.content.origin === 'string') {
      if (slot.content.origin) sessionIds.add(slot.content.origin);
    } else if (slot.content.origin && typeof slot.content.origin === 'object' && 'id' in slot.content.origin) {
      const originId = (slot.content.origin as { id?: string }).id;
      if (originId) sessionIds.add(originId);
    }
  });

  // 1. Get contributors with direct flow roles (respect eventSlot filter when present)
  allContributors.forEach((contributor) => {
    if (relatedContributors.has(contributor.id)) return;
    if (!contributor.roles?.length) return;
    if (contributor.roles.some(matchesFlowRole)) {
      relatedContributors.add(contributor.id);
      contributors.push(contributor);
    }
  });

  // 2. Get contributors with session roles in the flow's slots (respect eventSlot filter when present)
  allContributors.forEach((contributor) => {
    if (relatedContributors.has(contributor.id)) return;
    if (!contributor.roles?.length) return;
    if (contributor.roles.some((role) => matchesSessionRole(role, sessionIds))) {
      relatedContributors.add(contributor.id);
      contributors.push(contributor);
    }
  });

  return contributors;
}

/**
 * Gets all contributors that are NOT assigned to a specific flow
 * Useful for adding new contributors to flows
 */
export function getAvailableFlowContributors(flow: FlowDto, allContributors: ContributorDto[] = []): ContributorDto[] {
  const flowContributorIds = new Set(getFlowContributors(flow, allContributors).map((c) => c.id));
  return allContributors.filter((contributor) => !flowContributorIds.has(contributor.id));
}

/**
 * Gets all contributors that are NOT assigned to a specific session
 * Useful for adding new contributors to sessions
 */
export function getAvailableSessionContributors(
  session: SessionDto,
  allContributors: ContributorDto[] = [],
): ContributorDto[] {
  const sessionContributorIds = new Set(getSessionContributors(session, allContributors).map((c) => c.id));
  return allContributors.filter((contributor) => !sessionContributorIds.has(contributor.id));
}

/**
 * Counts roles that exist but are pending publication (not visible due to unpublished entities)
 * This helps distinguish between "no roles" and "roles pending publication"
 */
export function countRolesPendingPublication(
  contributor: ContributorDto,
  flowContext?: FlowDto,
  eventMultiFlow?: boolean,
): number {
  if (!contributor.roles || contributor.roles.length === 0) {
    return 0;
  }

  const flowId = flowContext?.origin || flowContext?.id;
  const currentFlowId = flowContext?.id;
  const sessionIds = new Set<string>();
  const flowSlotIds = new Set<string>();

  if (flowContext?.slots) {
    flowContext.slots.forEach((slot) => {
      flowSlotIds.add(slot.id);
      if (slot.contentType === 'Session' && slot.content) {
        if (typeof slot.content === 'string') {
          sessionIds.add(slot.content);
        } else if (typeof slot.content === 'object' && 'id' in slot.content) {
          sessionIds.add(slot.content.id);
          if ('origin' in slot.content && typeof slot.content.origin === 'string') {
            sessionIds.add(slot.content.origin);
          }
        }
      }
    });
  }

  const isRoleRelevant = (role: ContributorDto['roles'][0]) => {
    if (!flowContext) return true;
    if (eventMultiFlow === false) return true;
    if (!role.entity) return false;

    if (role.entityType === 'Flow') {
      return typeof role.entity === 'string'
        ? role.entity === flowId || role.entity === currentFlowId
        : role.entity?.id === flowId || role.entity?.id === currentFlowId;
    }

    if (role.entityType === 'Session') {
      const matches =
        typeof role.entity === 'string'
          ? sessionIds.has(role.entity)
          : role.entity?.id &&
            (sessionIds.has(role.entity.id) ||
              (typeof role.entity === 'object' &&
                'origin' in role.entity &&
                typeof role.entity.origin === 'string' &&
                sessionIds.has(role.entity.origin)));

      if (!matches) return false;

      return role.slots?.some((slot) => {
        if (slot.entityType !== 'FlowSlot') return false;
        const slotEntityId = typeof slot.entity === 'string' ? slot.entity : slot.entity?.id;
        return slotEntityId && flowSlotIds.has(slotEntityId);
      });
    }

    return false;
  };

  const isRolePending = (role: ContributorDto['roles'][0]) => {
    if (!role.entity) return false;
    if (role.entityType === 'Event') return false;
    if (typeof role.entity === 'string') return true;
    const hasTitle = !!(role.entity as FlowDto | EventDto | SessionDto).title;
    return !hasTitle;
  };

  return contributor.roles.reduce((count, role) => {
    if (!isRoleRelevant(role)) return count;
    return isRolePending(role) ? count + 1 : count;
  }, 0);
}

/**
 * Counts roles that exist but are pending publication for a specific contributor
 * Used when there are no visible roles to show the pending count
 * @param contributor The contributor to count pending roles for
 * @param flowContext Current flow context
 * @param eventMultiFlow Whether event is multi-flow
 * @param getEntityName Function to get entity name
 */
export function countRolesPendingPublicationForContributor(contributor: ContributorDto, flowContext?: FlowDto): number {
  if (!contributor.roles || contributor.roles.length === 0) {
    return 0;
  }

  // Count roles that are not visible (pending publication)
  let pendingCount = 0;

  contributor.roles.forEach((role) => {
    // Check if role would be filtered out
    if (!role.entity) return;

    // For published entities (object type), check if they have a proper title (not just ID)
    if (typeof role.entity === 'object' && role.entity) {
      const hasTitle = !!(role.entity as FlowDto | EventDto | SessionDto).title;

      if (!hasTitle) {
        pendingCount++;
        return;
      }
    }

    // Additional filtering based on context
    if (flowContext) {
      // In flow context, check if role is relevant to current flow
      const flowId = flowContext.origin || flowContext.id;
      const currentFlowId = flowContext.id;

      if (role.entityType === 'Flow') {
        const isRelevant =
          typeof role.entity === 'string'
            ? role.entity === flowId || role.entity === currentFlowId
            : role.entity?.id === flowId || role.entity?.id === currentFlowId;

        if (!isRelevant) {
          pendingCount++;
          return;
        }
      }

      if (role.entityType === 'Session') {
        // Check if session is in current flow's slots
        const sessionIds = new Set<string>();
        if (flowContext.slots) {
          flowContext.slots.forEach((slot) => {
            if (slot.contentType === 'Session' && slot.content) {
              if (typeof slot.content === 'string') {
                sessionIds.add(slot.content);
              } else if (typeof slot.content === 'object' && 'id' in slot.content) {
                sessionIds.add(slot.content.id);
                if ('origin' in slot.content && typeof slot.content.origin === 'string') {
                  sessionIds.add(slot.content.origin);
                }
              }
            }
          });
        }

        const isRelevant =
          typeof role.entity === 'string'
            ? sessionIds.has(role.entity)
            : role.entity?.id &&
              (sessionIds.has(role.entity.id) ||
                (typeof role.entity === 'object' &&
                  'origin' in role.entity &&
                  typeof role.entity.origin === 'string' &&
                  sessionIds.has(role.entity.origin)));

        if (!isRelevant) {
          pendingCount++;
          return;
        }
      }

      if (role.entityType === 'Event') {
        // Don't show Event roles in multi-flow context
        pendingCount++;
        return;
      }
    }
  });

  return pendingCount;
}

/**
 * Counts slots that exist but are pending publication (not visible due to unpublished flows)
 * This helps distinguish between "no slots" and "slots pending publication"
 * @param role The specific role to count slots for
 * @param getFlowForSlot Function to get flow for a slot
 */
export function countSlotsPendingPublicationForRole(
  role: ContributorDto['roles'][0],
  getFlowForSlot?: (slotId: string) => FlowDto | null,
): number {
  if (!role.slots || role.slots.length === 0) {
    return 0;
  }

  let totalSlots = 0;
  let visibleSlots = 0;

  if (role.entityType === 'Flow') {
    const hasFlowEntity = typeof role.entity === 'object' && role.entity !== null && !!(role.entity as FlowDto).title;
    if (!hasFlowEntity) {
      return role.slots.length;
    }
  }

  role.slots.forEach((slot) => {
    if (slot.entityType === 'FlowSlot') {
      totalSlots++;

      // Use the same logic as the component filtering
      if (getFlowForSlot) {
        const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
        const flowForSlot = getFlowForSlot(entityId);
        // Slot is visible if the flow exists and is published (has title)
        if (flowForSlot && flowForSlot.title) {
          visibleSlots++;
        }
      }
    }
  });

  return totalSlots - visibleSlots;
}

/** Ids that may appear on `role.entity` for Event roles (mode document vs origin). */
export function getAcceptedEventIdsForRoleMatch(
  event: { id?: string; origin?: unknown } | null | undefined,
): Set<string> {
  const s = new Set<string>();
  if (event?.id) s.add(event.id);
  if (event?.origin != null) {
    const o = getObjectId(event.origin as string | { id: string });
    if (o) s.add(o);
  }
  return s;
}

/** One crew assignment row for the crew widget (event or flow entity). */
export type CrewWidgetRow = {
  contributor: ContributorDto;
  role: RoleDto<ContributorRole>;
  entityType: 'Event' | 'Flow';
  entityId: string;
};

const matchesFlowCrewEntity = (entity: unknown, flowEntityId: string): boolean => {
  const id = typeof entity === 'string' ? entity : normalizeId(entity);
  const origin =
    typeof entity === 'object' && entity ? normalizeId((entity as { origin?: unknown }).origin) : undefined;
  return id === flowEntityId || origin === flowEntityId;
};

/**
 * Flow contributors with a given role name on the flow (any future flow crew role string).
 */
export function getFlowContributorsByRoleName(
  flow: FlowDto,
  roleName: string,
  allContributors: ContributorDto[] = [],
): ContributorDto[] {
  const contributors = getFlowContributors(flow, allContributors);
  const flowIds = new Set<string>([normalizeId(flow.id), normalizeId(flow.origin)].filter(Boolean) as string[]);
  return contributors.filter((contributor) =>
    contributor.roles.some((r) => {
      if (r.role !== roleName || r.entityType !== 'Flow') return false;
      if ((r as { deleted?: boolean }).deleted) return false;
      const id = typeof r.entity === 'string' ? r.entity : normalizeId(r.entity);
      const origin =
        typeof r.entity === 'object' && r.entity ? normalizeId((r.entity as { origin?: unknown }).origin) : undefined;
      return (id !== undefined && flowIds.has(id)) || (origin !== undefined && flowIds.has(origin));
    }),
  );
}

/** True if this role row points at the current event (any mode document id or origin id). */
export const matchesEventCrewEntity = (entity: unknown, acceptedEventIds: Set<string>): boolean => {
  if (acceptedEventIds.size === 0) return false;
  const id = normalizeId(entity);
  if (id && acceptedEventIds.has(id)) return true;
  const origin =
    typeof entity === 'object' && entity ? normalizeId((entity as { origin?: unknown }).origin) : undefined;
  if (origin && acceptedEventIds.has(origin)) return true;
  return false;
};

const isEventCrewRoleName = (roleName: string): boolean => (EVENT_CREW_ROLES as readonly string[]).includes(roleName);

/** True when contributor already has an active event crew role on this event (only one allowed per event). */
export function contributorHasActiveEventCrewOnEvent(
  contributor: Pick<ContributorDto, 'roles'>,
  acceptedEventIds: Set<string>,
): boolean {
  return contributor.roles.some((r) => {
    if ((r as { deleted?: boolean }).deleted) return false;
    if (r.entityType !== 'Event' || !isEventCrewRoleName(r.role as string)) return false;
    return matchesEventCrewEntity(r.entity, acceptedEventIds);
  });
}

/** Same assignment row (event crew compares by event, not role name). */
export function contributorRolesMatchAssignment(
  contributorRole: { entity?: unknown; entityType?: string; role?: string; deleted?: boolean },
  assignment: { entity: string; entityType: string; role: string },
  acceptedEventIds: Set<string>,
): boolean {
  if ((contributorRole as { deleted?: boolean }).deleted) return false;
  if (contributorRole.entityType !== assignment.entityType || contributorRole.role !== assignment.role) {
    return false;
  }
  if (
    assignment.entityType === 'Event' &&
    isEventCrewRoleName(assignment.role) &&
    isEventCrewRoleName(contributorRole.role as string)
  ) {
    return matchesEventCrewEntity(contributorRole.entity, acceptedEventIds);
  }
  if (contributorRole.entity == null) return false;
  return getObjectId(contributorRole.entity as string | { id: string }) === assignment.entity;
}

/**
 * Whether the contributor already has this exact assignment (used to skip POST /roles).
 */
export function contributorAlreadyHasRoleAssignment(
  contributor: Pick<ContributorDto, 'roles'>,
  assignment: { entity: string; entityType: string; role: string },
  acceptedEventIds: Set<string>,
): boolean {
  return contributor.roles.some((r) => contributorRolesMatchAssignment(r, assignment, acceptedEventIds));
}

/**
 * @param acceptedEventIds Event `id` and, when present, `origin` id — roles may reference either (PLAN vs ORGANIZE/PERFORM docs).
 */
export function getEventCrewRows(
  contributors: ContributorDto[],
  roleFilter: readonly string[],
  acceptedEventIds: Set<string>,
): CrewWidgetRow[] {
  if (acceptedEventIds.size === 0) return [];
  const rows: CrewWidgetRow[] = [];
  const allowed = new Set(roleFilter);
  for (const c of contributors) {
    for (const r of c.roles) {
      if ((r as { deleted?: boolean }).deleted) continue;
      if (r.entityType !== 'Event' || !allowed.has(r.role as string)) continue;
      if (!matchesEventCrewEntity(r.entity, acceptedEventIds)) continue;
      const entityId = normalizeId(r.entity) ?? [...acceptedEventIds][0];
      rows.push({ contributor: c, role: r, entityType: 'Event', entityId });
    }
  }
  return rows;
}

export function getFlowCrewRows(
  flow: FlowDto,
  contributors: ContributorDto[],
  flowCrewRoles: readonly string[],
): CrewWidgetRow[] {
  const flowEntityId = normalizeId(flow.origin) || normalizeId(flow.id);
  if (!flowEntityId) return [];
  const rows: CrewWidgetRow[] = [];
  for (const roleName of flowCrewRoles) {
    const candidates = getFlowContributorsByRoleName(flow, roleName, contributors);
    for (const c of candidates) {
      const r = c.roles.find(
        (role) =>
          role.role === roleName &&
          role.entityType === 'Flow' &&
          !(role as { deleted?: boolean }).deleted &&
          matchesFlowCrewEntity(role.entity, flowEntityId),
      );
      if (r) {
        rows.push({ contributor: c, role: r, entityType: 'Flow', entityId: flowEntityId });
      }
    }
  }
  return rows;
}

/**
 * Crew rows for flow settings: optional event-level crew (single-flow) plus flow-level crew roles.
 */
export function getFlowSettingsCrewRows(
  flow: FlowDto,
  acceptedEventIds: Set<string>,
  contributors: ContributorDto[],
  eventMultiFlow: boolean | undefined,
  flowCrewRoles: readonly string[],
): CrewWidgetRow[] {
  const flowRows = getFlowCrewRows(flow, contributors, flowCrewRoles);
  if (eventMultiFlow !== false) {
    return flowRows;
  }
  const eventRows = getEventCrewRows(contributors, [...EVENT_CREW_ROLES], acceptedEventIds);
  const key = (row: CrewWidgetRow) => `${row.contributor.id}-${row.role.id}`;
  const seen = new Set(flowRows.map(key));
  const merged = [...flowRows];
  for (const row of eventRows) {
    const k = key(row);
    if (!seen.has(k)) {
      seen.add(k);
      merged.push(row);
    }
  }
  return merged;
}
